// Document Writer Sidebar - Dedicated sidebar for document generation and refinement
// Uses DocumentWriterContext for independent state management

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useMutation, useQuery } from "convex/react";
import { useUser } from "@clerk/clerk-react";
import { api } from "@/convex/_generated/api";
import {
  Send,
  Bot,
  User,
  Loader2,
  X,
  AlertCircle,
  Plus,
  History,
  ArrowLeft,
  Trash2,
  FileText,
  Square,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useDocumentWriter } from "@/contexts/DocumentWriterContext";
import { ChatErrorBoundary } from "../chat/ChatErrorBoundary";
import { StreamingMessage } from "../agent/StreamingMessage";
import { useToast } from "@/components/ui/use-toast";
import type { UIMessage } from "@convex-dev/agent/react";
import { UpgradePromptDialog } from "@/components/billing/UpgradePromptDialog";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface DocumentWriterSidebarProps {
  className?: string;
  fillParent?: boolean;
  hasActiveDocument?: boolean; // Whether there's an active document being generated/refined
}

interface DocumentThread {
  _id: string;
  title: string;
  preview: string;
  createdAt: number;
}

function DocumentWriterSidebarContent({
  className,
  fillParent = false,
  hasActiveDocument = false,
}: DocumentWriterSidebarProps) {
  // ===== HOOKS =====
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();
  const { threadId, setThreadId, clearThread, isSidebarVisible, hideSidebar, triggerCanvasScroll } =
    useDocumentWriter();
  const { toast } = useToast();

  // Local state
  const [prompt, setPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(!hasActiveDocument);
  const [deletingThreadId, setDeletingThreadId] = useState<string | null>(null);
  const [threadToDelete, setThreadToDelete] = useState<string | null>(null);

  const [showCreditAlert, setShowCreditAlert] = useState(false);

  // Fetch usage data
  const usage = useQuery(api.usage.getUsage);

  const subscription = useQuery(api.subscriptions.getUserSubscription, user ? {} : "skip");
  const isFreePlan = subscription !== undefined && (!subscription?.amount || subscription.amount === 0);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // ===== CONVEX MUTATIONS =====
  const refineDocument = useMutation(api.documentWriterStreaming.refineDocument);
  const deleteThreadMutation = useMutation(api.documentWriterStreaming.deleteDocumentThread);

  // ===== CONVEX QUERIES =====
  // Fetch messages for current thread
  const messagesResponse = useQuery(
    api.documentWriterStreaming.listDocumentMessages,
    threadId
      ? {
          threadId,
          paginationOpts: { cursor: null, numItems: 100 },
          streamArgs: { kind: "list" as const },
        }
      : "skip"
  );

  // Fetch thread history (only Document Writer threads with documents)
  const threadsQuery = useQuery(
    api.documentWriterStreaming.listDocumentThreads,
    showHistory ? { paginationOpts: { cursor: null, numItems: 50 } } : "skip"
  );

  // ===== MESSAGE PROCESSING =====
  const allMessages = messagesResponse?.page || [];

  /**
   * Sanitize user messages to strip internal metadata (dates, IDs, system instructions)
   * that the backend prepends for the AI agent but should not be shown in the chat UI.
   *
   * Handles two patterns:
   * 1. Initial prompt: "CURRENT_DATE: ...\n\nUSER REQUEST:\n[CUSTOM] actual prompt"
   * 2. Refinement context: "[SYSTEM CONTEXT]\n..." (entire message is system context)
   */
  const sanitizeUserMessage = (text: string): string | null => {
    if (!text) return null;

    // Pattern 1: Combined prompt with "USER REQUEST:" marker (check first!)
    const userRequestMarker = "USER REQUEST:\n";
    const markerIndex = text.indexOf(userRequestMarker);
    if (markerIndex !== -1) {
      let cleaned = text.substring(markerIndex + userRequestMarker.length).trim();
      // Strip [CUSTOM] or [TEMPLATE: xxx] prefix tags
      cleaned = cleaned.replace(/^\[CUSTOM\]\s*/, "");
      cleaned = cleaned.replace(/^\[TEMPLATE:\s*[^\]]*\]\s*/, "");
      return cleaned || text; // Fallback to original if cleaned is empty
    }

    // Pattern 2: Standalone [SYSTEM CONTEXT] messages (without a USER REQUEST) — hide entirely
    if (text.trimStart().startsWith("[SYSTEM CONTEXT]")) {
      return null; // Signal to filter this message out
    }

    // Pattern 3: Fallback — strip known metadata lines by regex if marker is missing
    let cleaned = text;
    // Remove CURRENT_DATE / TODAY_ISO lines
    cleaned = cleaned.replace(/^CURRENT_DATE:.*$/m, "");
    cleaned = cleaned.replace(/^TODAY_ISO:.*$/m, "");
    // Remove IMPORTANT date instruction block (starts with "IMPORTANT:" and references CURRENT_DATE)
    cleaned = cleaned.replace(/^IMPORTANT:.*CURRENT_DATE.*$/gm, "");
    // Remove BID_ID / CLIENT_ID lines
    cleaned = cleaned.replace(/^BID_ID:.*$/m, "");
    cleaned = cleaned.replace(/^CLIENT_ID:.*$/m, "");
    // Remove BRAND_COLORS line
    cleaned = cleaned.replace(/^BRAND_COLORS:.*$/m, "");
    // Remove USE_TEMPLATE_ID / TEMPLATE_NAME lines
    cleaned = cleaned.replace(/^USE_TEMPLATE_ID:.*$/m, "");
    cleaned = cleaned.replace(/^TEMPLATE_NAME:.*$/m, "");
    // Strip [CUSTOM] or [TEMPLATE: xxx] prefix tags
    cleaned = cleaned.replace(/^\[CUSTOM\]\s*/, "");
    cleaned = cleaned.replace(/^\[TEMPLATE:\s*[^\]]*\]\s*/, "");
    // Collapse multiple blank lines
    cleaned = cleaned.replace(/\n{3,}/g, "\n\n").trim();

    return cleaned || text; // Fallback to original if cleaned is empty
  };

  // Filter UIMessages to only show conversational messages (not long document content)
  // Keep UIMessage format for StreamingMessage component compatibility
  const messages: UIMessage[] = React.useMemo(() => {
    if (!allMessages || allMessages.length === 0) {
      return [];
    }

    // Markers that indicate internal retry/technical messages that shouldn't be shown to users
    const INTERNAL_MESSAGE_MARKERS = [
      "⚠️ DOCUMENT REGENERATION REQUIRED",
      "COMPILATION ERRORS TO FIX",
      "corrected Typst",
      "Typst syntax",
      "Typst code",
    ];

    return allMessages
      .filter((msg: UIMessage) => {
        // Filter out undefined/null messages
        if (!msg || !msg.role || !msg.text) return false;

        const messageText = msg.text || "";

        // Filter out internal retry/technical messages (regardless of role)
        const isInternalMessage = INTERNAL_MESSAGE_MARKERS.some((marker) =>
          messageText.includes(marker)
        );
        if (isInternalMessage) return false;

        // For user messages, check if they're pure system context (should be hidden)
        if (msg.role === "user") {
          const sanitized = sanitizeUserMessage(messageText);
          if (sanitized === null) return false; // Filter out [SYSTEM CONTEXT] messages
          return true;
        }

        // For assistant messages, filter out very long document content AND failed messages.
        // Failed messages (e.g. from a generation error) should NOT be shown in the chat;
        // those errors are already logged to the server console via console.error.
        if (msg.role === "assistant") {
          if (msg.status === "failed") {
            console.error(
              "[DocumentWriter] Suppressing failed assistant message from UI (already logged server-side):",
              msg.key
            );
            return false;
          }
          const textLength = messageText.length || 0;
          return textLength < 2000; // Show messages up to 2000 characters
        }

        return false;
      })
      .map((msg: UIMessage) => {
        // Sanitize user message text to remove system metadata
        if (msg.role === "user" && msg.text) {
          const sanitized = sanitizeUserMessage(msg.text);
          if (sanitized && sanitized !== msg.text) {
            // Return a new object with sanitized text (avoid mutating the original)
            return { ...msg, text: sanitized };
          }
        }
        return msg;
      });
  }, [allMessages]);

  // Determine if agent is currently streaming
  const isStreaming = messages.some((msg) => msg.status === "streaming");
  const isGenerating = isLoading || isStreaming;

  // ===== EFFECTS =====
  // Auto-show history when there's no active document
  useEffect(() => {
    if (!hasActiveDocument) {
      setShowHistory(true);
    } else {
      setShowHistory(false);
    }
  }, [hasActiveDocument]);

  // Auto-scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [prompt]);

  // ===== HANDLERS =====
  const handleSendMessage = useCallback(async () => {
    if (!prompt.trim() || isLoading || !threadId) return;

    const purchasedCredits = (usage?.purchasedCredits ?? (subscription as any)?.purchasedCredits ?? 0);
    const hasPurchasedCredits = purchasedCredits > 0;
    const monthlyRemaining = usage ? Math.max(0, usage.creditLimit - usage.creditsUsed) : 0;
    const hasActivePaidPlan = Boolean(subscription && subscription.status === "active" && (subscription.amount || 0) > 0);

    // Only block if user has no top-up credits AND no remaining monthly plan credits
    if (!hasPurchasedCredits && (!hasActivePaidPlan || monthlyRemaining <= 0)) {
      setShowUpgradeModal(true);
      return;
    }

    const messageContent = prompt.trim();
    setPrompt("");
    setIsLoading(true);
    setError(null);

    try {
      console.log("[DocumentWriter] Sending refinement request");
      // Scroll the document canvas to top so user sees the regeneration starting
      triggerCanvasScroll();
      await refineDocument({ threadId, prompt: messageContent });
    } catch (error: any) {
      console.error("[DocumentWriter] Failed to send message:", error);

      // Check if this is a usage limit exceeded error
      const errorMessage = error?.data?.message || error?.message || "";
      const errorCode = error?.data?.code || "";

      if (
        errorCode === "USAGE_LIMIT_EXCEEDED" ||
        errorMessage.includes("credit limit") ||
        errorMessage.includes("exceeded")
      ) {
        setShowUpgradeModal(true);
      } else {
        setError("Failed to send message. Please try again.");
      }
      setPrompt(messageContent); // Restore the message
    } finally {
      setIsLoading(false);
    }
  }, [prompt, isLoading, threadId, refineDocument, usage, triggerCanvasScroll]);

  const handleNewDocument = useCallback(() => {
    clearThread();
    setPrompt("");
    setError(null);
    setShowHistory(false);
    navigate("/document-writer"); // Navigate to document writer input view
  }, [clearThread, navigate]);

  const handleShowHistory = useCallback(() => {
    console.log("[DocumentWriter] Showing history");
    setShowHistory(true);
    setError(null);
  }, []);

  const handleBackToChat = useCallback(() => {
    setShowHistory(false);
  }, []);

  const handleSwitchThread = useCallback(
    (newThreadId: string) => {
      console.log("[DocumentWriter] Switching to thread:", newThreadId);
      setThreadId(newThreadId);
      setShowHistory(false);
      setError(null);

      // Navigate to document writer with the thread
      navigate("/document-writer", {
        state: {
          loadThreadId: newThreadId,
          hasDocument: true,
          fromChatHistory: true,
        },
      });
    },
    [setThreadId, navigate]
  );

  const confirmDeleteThread = useCallback(
    (threadIdToDelete: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setThreadToDelete(threadIdToDelete);
    },
    []
  );

  const executeDeleteThread = useCallback(
    async (e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
      }
      if (!threadToDelete) return;

      try {
        setDeletingThreadId(threadToDelete);
        await deleteThreadMutation({ threadId: threadToDelete });

        // If we deleted the current thread, clear it
        if (threadToDelete === threadId) {
          clearThread();
          setShowHistory(false);
        }

        toast({
          title: "Document deleted",
          description: "The document and conversation have been deleted.",
          duration: 3000,
        });
      } catch (error) {
        console.error("[DocumentWriter] Failed to delete thread:", error);
        toast({
          title: "Delete failed",
          description: "Failed to delete the document. Please try again.",
          variant: "destructive",
          duration: 5000,
        });
      } finally {
        setDeletingThreadId(null);
        setThreadToDelete(null);
      }
    },
    [threadToDelete, threadId, deleteThreadMutation, clearThread, toast]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleStop = useCallback(() => {
    setIsLoading(false);
  }, []);

  // ===== EARLY RETURNS (after all hooks) =====
  if (!isLoaded) {
    return (
      <div className={cn("flex h-full items-center justify-center", className)}>
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!user) {
    return (
      <div
        className={cn(
          "flex h-full flex-col items-center justify-center p-6 text-center",
          className
        )}
      >
        <AlertCircle className="mb-4 h-12 w-12 text-purple-600" />
        <p className="text-gray-600">Please sign in to use the Document Writer</p>
      </div>
    );
  }

  if (!isSidebarVisible) {
    return null;
  }

  // ===== RENDER =====
  return (
    <div
      className={cn(
        "flex h-full flex-col border-l border-gray-200 bg-white",
        fillParent ? "w-full" : "w-96",
        className
      )}
    >
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-violet-50 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100">
              <Bot className="h-4 w-4 text-purple-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Document Writer</h2>
              <p className="text-xs text-gray-600">Refine and edit your documents</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={hideSidebar}
              className="h-8 w-8 p-0 hover:bg-white/50"
              title="Close Sidebar"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {showHistory ? (
          // History View
          <div className="flex-1 overflow-y-auto p-4">
            {/* Only show "Back to Chat" button if there's an active document */}
            {hasActiveDocument && (
              <div className="mb-4 flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToChat}
                  className="text-purple-600 hover:text-purple-700"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Chat
                </Button>
              </div>
            )}

            {/* Show empty state if no threads */}
            {threadsQuery === undefined ? (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
              </div>
            ) : !threadsQuery.page || threadsQuery.page.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center p-6 text-center">
                <History className="mb-4 h-12 w-12 text-purple-300" />
                <p className="mb-2 text-gray-600">No document history yet</p>
                <p className="text-sm text-gray-500">Create your first document to get started</p>
              </div>
            ) : (
              <>
                <h3 className="mb-4 px-1 text-lg font-semibold text-gray-900">
                  Conversation History
                </h3>
                <div className="space-y-3">
                  {threadsQuery.page.map((thread: DocumentThread) => (
                    <div
                      key={thread._id}
                      onClick={() => handleSwitchThread(thread._id)}
                      className={cn(
                        "group cursor-pointer rounded-xl border-2 p-4 transition-all",
                        threadId === thread._id
                          ? "border-purple-200 bg-purple-50 shadow-sm"
                          : "border-gray-100 bg-white hover:border-purple-200 hover:bg-gray-50"
                      )}
                    >
                      <div className="mb-2 flex items-start justify-between">
                        <div className="flex items-center gap-3 overflow-hidden">
                          <div className="flex-shrink-0 rounded-lg bg-purple-50 p-2 text-purple-600">
                            <FileText className="h-4 w-4" />
                          </div>
                          <h4 className="truncate font-medium text-gray-900">{thread.title}</h4>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-2">
                          {thread._id === threadId && (
                            <span className="rounded bg-purple-600 px-2 py-0.5 text-xs font-medium text-white">
                              Active
                            </span>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => confirmDeleteThread(thread._id, e)}
                            disabled={deletingThreadId === thread._id}
                            className={cn(
                              "h-7 w-7 p-0 opacity-0 transition-opacity group-hover:opacity-100",
                              deletingThreadId === thread._id && "opacity-100"
                            )}
                            title="Delete conversation"
                          >
                            {deletingThreadId === thread._id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 pl-11 text-xs text-gray-500">
                        <span>
                          {new Date(thread.createdAt).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          // Chat View
          <>
            {/* Messages */}
            <div className="flex-1 space-y-4 overflow-y-auto p-4">
              {error && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
                  <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-600" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {messages.length === 0 && !isStreaming && (
                <div className="flex h-full flex-col items-center justify-center p-6 text-center">
                  <FileText className="mb-4 h-12 w-12 text-purple-300" />
                  <p className="mb-2 text-gray-600">No messages yet</p>
                  <p className="text-sm text-gray-500">Send a message to refine your document</p>
                </div>
              )}

              {messages.map((message) => (
                <StreamingMessage
                  key={message.key}
                  message={message}
                  theme="purple"
                  threadId={threadId ?? undefined}
                  agentType="document-writer"
                  onUpdate={scrollToBottom}
                />
              ))}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="border-t border-gray-200 bg-white p-4">
              <div className="mb-3 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNewDocument}
                  className="flex-1 border-purple-200 text-purple-600 hover:bg-purple-50"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  New Document
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleShowHistory}
                  className="flex-1 border-purple-200 text-purple-600 hover:bg-purple-50"
                >
                  <History className="mr-2 h-4 w-4" />
                  History
                </Button>
              </div>

              {/* Only show chat input when there's an active document */}
              {hasActiveDocument ? (
                <div className="flex gap-2">
                  <Textarea
                    ref={textareaRef}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      threadId
                        ? "Ask for changes to your document..."
                        : "Select a document to refine it"
                    }
                    disabled={isLoading || !threadId}
                    className="max-h-[200px] min-h-[40px] flex-1 resize-none"
                    rows={1}
                  />
                  <Button
                    onClick={isGenerating ? handleStop : handleSendMessage}
                    disabled={!prompt.trim() && !isGenerating}
                    className={cn(
                      "self-end text-white transition-all",
                      isGenerating
                        ? "bg-red-500 hover:bg-red-600"
                        : "bg-purple-600 hover:bg-purple-700"
                    )}
                  >
                    {isGenerating ? (
                      <Square className="h-5 w-5 fill-current" />
                    ) : (
                      <Send className="h-5 w-5" />
                    )}
                  </Button>
                </div>
              ) : (
                <div className="rounded-lg border border-purple-100 bg-purple-50 px-4 py-3 text-center">
                  <p className="text-sm text-purple-700">
                    Create a new document or select one from history to start refining
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>


      <UpgradePromptDialog
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Upgrade to Starter"
        description="Use the AI Document Writer to refine your documents and ask for customized changes."
        feature="Document Writer Chat"
        planName="Starter"
        planFeatures={["Unlimited Bids & Proposals", "Connect apps like Gmail & Notion", "Advanced Analytics"]}
      />

      <AlertDialog open={!!threadToDelete} onOpenChange={(open) => !open && setThreadToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the document and conversation history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingThreadId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={executeDeleteThread}
              disabled={!!deletingThreadId}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingThreadId ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function DocumentWriterSidebar(props: DocumentWriterSidebarProps) {
  return (
    <ChatErrorBoundary>
      <DocumentWriterSidebarContent {...props} />
    </ChatErrorBoundary>
  );
}
